import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Textarea } from "components/ui/textarea";
import { Loader2 } from "lucide-react";
import { toast } from "components/ui/toast";
import { useUserInfo } from "core";
import { useCreateSupportTicket } from "core";

import {
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
} from "components/ui/select";

interface SupportModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export default function SupportForm({ open, onOpenChange }: SupportModalProps) {
    const { data: user, isLoading: userLoading } = useUserInfo();
    const createTicket = useCreateSupportTicket();

    const [subject, setSubject] = useState("");
    const [description, setDescription] = useState("");
    const [errors, setErrors] = useState({ subject: "", description: "" });
    const [touched, setTouched] = useState({ subject: false, description: false });

    useEffect(() => {
        console.log("user value in here see --->", user);
    }, [user]);

    useEffect(() => {
        if (!open) {
            setSubject("");
            setDescription("");
            setErrors({ subject: "", description: "" });
            setTouched({ subject: false, description: false });
        }
    }, [open]);

    const validateForm = () => {
        const newErrors = { subject: "", description: "" };
        let isValid = true;

        if (!subject.trim()) {
            newErrors.subject = "Please select a subject";
            isValid = false;
        }

        if (!description.trim()) {
            newErrors.description = "Please provide a description";
            isValid = false;
        }

        setErrors(newErrors);
        setTouched({ subject: true, description: true });
        return isValid;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        createTicket.mutate(
            {
                subject,
                description,
                email: user?.attrs?.email ?? "anonymous@user.com",
            },
            {
                onSuccess: () => {
                    toast.success("Your query has been submitted successfully!");
                    setSubject("");
                    setDescription("");
                    setErrors({ subject: "", description: "" });
                    setTouched({ subject: false, description: false });
                    onOpenChange(false);
                },
                onError: (error: any) => {
                    console.error("Support ticket error:", error);
                    toast.error(
                        error?.response?.data?.description ||
                        "Something went wrong. Please try again."
                    );
                },
            }
        );
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-xl border-0">
                <DialogHeader>
                    <DialogTitle className="text-xl font-semibold text-foreground">
                        We're Here to Help!
                    </DialogTitle>
                    <DialogDescription>
                        Tell us your issue and we'll assist you promptly.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                    {/* Subject Dropdown */}
                    <div className="space-y-2">
                        <label
                            htmlFor="subject"
                            className="text-sm font-medium text-foreground"
                        >
                            Subject <span className="text-red-500 dark:text-red-400">*</span>
                        </label>

                        <Select
                            value={subject}
                            onValueChange={(value) => {
                                setSubject(value);
                                if (touched.subject && !value.trim()) {
                                    setErrors((prev) => ({ ...prev, subject: "Please select a subject" }));
                                } else {
                                    setErrors((prev) => ({ ...prev, subject: "" }));
                                }
                            }}
                        >
                            <SelectTrigger
                                id="subject"
                                className={`w-full ${touched.subject && errors.subject ? "border-red-500 focus:ring-red-500" : ""}`}
                                onBlur={() => setTouched((prev) => ({ ...prev, subject: true }))}
                            >
                                <SelectValue
                                    placeholder="-- Select Subject --"
                                    className="text-content-muted"
                                />
                            </SelectTrigger>

                            <SelectContent>
                                <SelectItem value="billing">Billing</SelectItem>
                                <SelectItem value="technical">Technical</SelectItem>
                                <SelectItem value="subscription">Subscription</SelectItem>
                                <SelectItem value="general_question">
                                    General Question
                                </SelectItem>
                            </SelectContent>
                        </Select>
                        {touched.subject && errors.subject && (
                            <p className="text-sm text-red-500 dark:text-red-400 mt-1">{errors.subject}</p>
                        )}
                    </div>

                    {/* Description */}
                    <div className="space-y-2">
                        <label
                            htmlFor="description"
                            className="text-sm font-medium text-foreground"
                        >
                            Description <span className="text-red-500 dark:text-red-400">*</span>
                        </label>
                        <Textarea
                            id="description"
                            value={description}
                            onChange={(e) => {
                                setDescription(e.target.value);
                                if (touched.description && !e.target.value.trim()) {
                                    setErrors((prev) => ({ ...prev, description: "Please provide a description" }));
                                } else {
                                    setErrors((prev) => ({ ...prev, description: "" }));
                                }
                            }}
                            onBlur={() => setTouched((prev) => ({ ...prev, description: true }))}
                            placeholder="Please describe your issue"
                            rows={5}
                            className={touched.description && errors.description ? "border-red-500 focus:ring-red-500" : ""}
                        />
                        {touched.description && errors.description && (
                            <p className="text-sm text-red-500 dark:text-red-400 mt-1">{errors.description}</p>
                        )}
                    </div>

                    {/* Submit Button */}
                    <DialogFooter className="pt-2">
                        <Button
                            type="submit"
                            className="w-full"
                            disabled={createTicket.isPending || userLoading}
                        >
                            {createTicket.isPending || userLoading ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" /> Submitting...
                                </>
                            ) : (
                                "Submit"
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
